import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRealtime } from "../realtime/RealtimeContext";
import type { LueckentextExerciseConfig } from "@calega/content";
import { getExerciseRunStatus, submitExerciseAnswers } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";
import type { LearnerRunSummary } from "@calega/exercise-engine";

function normalize(value: string) { return value.trim().toLocaleLowerCase("de-DE"); }

export function Lueckentext({ pageId, config }: { pageId: string; config: LueckentextExerciseConfig }) {
  const startExerciseRun = useRecommendationRunStarter();
  const starter = useRef(startExerciseRun); starter.current = startExerciseRun;
  const realtime = useRealtime();
  const completion = useRunCompletion();
  const gaps = config.parts.filter((part) => part.type === "gap");
  const [values, setValues] = useState<Record<string, string>>({});
  const [checked, setChecked] = useState(false);
  const [remoteCompleted, setRemoteCompleted] = useState(false);
  const [savedRun, setSavedRun] = useState<LearnerRunSummary | null>(null);
  const [runId, setRunId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const pendingStart = useRef<Promise<string> | null>(null);
  const preparePause = useCallback(async () => {
    if (runId) return runId;
    if (pendingStart.current) return pendingStart.current;
    setBusy(true);
    pendingStart.current = starter.current<{ runId: string }>({ contentPageId: pageId }).then((result) => { setRunId(result.runId); return result.runId; });
    try { return await pendingStart.current; } finally { pendingStart.current = null; setBusy(false); }
  }, [runId, pageId]);
  const follower = realtime.session?.participantRole === "PARTICIPANT";
  useLearningDisplayItem(runId, "0", !checked, follower ? undefined : preparePause);
  useEffect(() => {
    if (!realtime.session) return;
    const envelope = realtime.state.exercise as { exerciseType?: string; pageId?: string; runId?: string; state?: unknown } | null;
    if (envelope?.exerciseType === "lueckentext" && envelope.pageId === pageId && envelope.runId) {
      if (envelope.runId !== runId) {
        if (runId) reset(envelope.runId);
        else setRunId(envelope.runId);
      }
      return;
    }
    if (follower && envelope?.exerciseType === "lueckentext" && envelope.pageId === pageId && !envelope.state) reset();
    if (follower) return;
    let cancelled = false;
    void preparePause().then((id) => {
      if (!cancelled) realtime.publishExercise({ exerciseType: "lueckentext", pageId, runId: id, state: { runId: id, phase: "running" } });
    }).catch((cause) => { if (!cancelled) setMessage(cause.message); });
    return () => { cancelled = true; };
  }, [realtime.session?.id, realtime.state.exercise, pageId, follower]);
  useEffect(() => {
    if (!realtime.session || !runId || checked) return;
    const controller = new AbortController();
    let pending = false;
    const checkStatus = async () => {
      if (document.visibilityState !== "visible" || pending || controller.signal.aborted) return;
      pending = true;
      try {
        const status = await getExerciseRunStatus(runId, controller.signal);
        if (!controller.signal.aborted && status.status === "COMPLETED") {
          setRemoteCompleted(true);
          setChecked(true);
        }
      } catch { /* A failed read never changes the local completion state. */ }
      finally { pending = false; }
    };
    const timer = window.setInterval(() => void checkStatus(), 5_000);
    const wake = () => void checkStatus();
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    void checkStatus();
    return () => { controller.abort(); window.clearInterval(timer); document.removeEventListener("visibilitychange", wake); window.removeEventListener("focus", wake); };
  }, [realtime.session?.id, runId, checked]);
  const correct = gaps.filter((gap) => normalize(values[gap.id] ?? "") === normalize(gap.answer)).length;

  async function check() {
    if (busy || checked) return;
    setBusy(true); setMessage("");
    try {
      const activeRunId = runId || (await startExerciseRun<{ runId: string }>({ contentPageId: pageId })).runId;
      setRunId(activeRunId);
      await submitExerciseAnswers(activeRunId, gaps.map((gap) => ({ type: "lueckentext" as const, gapId: gap.id, value: values[gap.id] ?? "" })));
      await completion.complete(activeRunId, () => { setRemoteCompleted(false); setChecked(true); });
    } catch (error) { setMessage(error instanceof Error ? error.message : "Die Antworten konnten nicht gespeichert werden."); }
    finally { setBusy(false); }
  }

  function reset(nextRunId = "") {
    setValues({});
    setChecked(false);
    setRemoteCompleted(false);
    setSavedRun(null);
    setRunId(nextRunId);
    setMessage("");
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!runId || !checked) return;
    if (repeat) {
      const next = await startExerciseRun<{ runId: string }>({ contentPageId: pageId });
      reset(next.runId);
      if (realtime.session && !follower) realtime.publishExercise({ exerciseType: "lueckentext", pageId, runId: next.runId, state: { runId: next.runId, phase: "running" } });
    } else {
      reset();
      if (realtime.session && !follower) realtime.publishExercise({ exerciseType: "lueckentext", pageId, runId: "", state: null });
    }
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  return <section className="lueckentext-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    <div className="lueckentext-card learning-text">{config.parts.map((part, index) => {
      if (part.type === "text") return <span key={index}>{part.text}</span>;
      const isCorrect = normalize(values[part.id] ?? "") === normalize(part.answer);
      const className = checked && !remoteCompleted ? (isCorrect ? "correct" : "wrong") : "";
      const value = checked && !remoteCompleted && !isCorrect ? part.answer : values[part.id] ?? "";
      return part.options ? <select key={part.id} className={className} value={value} disabled={checked || busy} onChange={(event) => setValues((current) => ({ ...current, [part.id]: event.target.value }))}><option value="">— wählen —</option>{part.options.map((option) => <option key={option} value={option}>{option}</option>)}</select> : <input key={part.id} className={className} value={value} disabled={checked || busy} aria-label={`Lücke ${index + 1}`} onChange={(event) => setValues((current) => ({ ...current, [part.id]: event.target.value }))} onKeyDown={(event) => { if (event.key === "Enter" && !checked) void check(); }} />;
    })}</div>
    {checked && <div className={`lueckentext-result ${savedRun ? savedRun.score === savedRun.maxScore ? "perfect" : "" : !remoteCompleted && correct === gaps.length ? "perfect" : ""}`}>
      {savedRun ? <strong>{savedRun.score} von {savedRun.maxScore} richtig</strong> : !remoteCompleted && <strong>{correct} von {gaps.length} richtig</strong>}
      {!remoteCompleted && correct < gaps.length && <p>Die richtige Lösung ist jeweils in der roten Lücke sichtbar.</p>}<p>Der Durchlauf ist abgeschlossen.</p>
    </div>}
    <div className="lueckentext-actions">{!checked ? <button className="primary" disabled={busy || Boolean(follower && !runId)} onClick={() => void check()}>Überprüfen</button> : follower ? <p>Die Sitzungsleitung kann einen neuen Durchgang starten.</p> : <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}</div>
    {checked && <CompletedRunHistory key={runId} runId={runId} onLoaded={setSavedRun} />}
    {message && <div className="alert error">{message}</div>}
  </section>;
}
